import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";

interface ActionSummary {
  actionId: string;
  actionName: string;
  actionType: string;
  researchSummary: {
    success?: boolean;
    summary?: string;
  } | null;
  researchSteps: string[];
  createdDate: string;
  updatedDate: string;
}

export default ({
  open,
  onClose,
  listId,
  selectedAction,
  companyId,
  getToken,
  eventListener,
}: {
  open: boolean;
  onClose: () => void;
  selectedAction: any;
  listId: string;
  companyId: string;
  getToken: Function;
  eventListener: Function;
}) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ActionSummary | null>(null);

  const handleClose = () => {
    setSummary(null);
    onClose();
  };

  const loadSummary = async () => {
    if (!selectedAction?.id) return;
    
    const currentToken = await getToken();
    setLoading(true);
    axios
      .get(
        `${hostname}/lists/${listId}/action/${selectedAction.id}/summary`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then((res) => {
        setSummary(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: "Failed to load action summary",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedAction && open) {
      loadSummary();
    }
  }, [selectedAction, open]);

  if (!selectedAction) {
    return <></>;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Action Summary: {selectedAction.name}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {!loading && !summary && (
          <Typography>No summary found</Typography>
        )}
        {!loading && summary && (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {summary.actionType === "add_rows" ? "Add Rows Action" : "Update Rows Action"} Summary
            </Typography>

            {summary.researchSteps && summary.researchSteps.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Research Steps
                </Typography>
                {summary.researchSteps.map((step: string, i: number) => (
                  <Card sx={{ mt: 2 }} key={i}>
                    <CardContent>
                      <Typography gutterBottom>Step #{i + 1}</Typography>
                      <Typography sx={{ whiteSpace: "pre-wrap" }}>{step}</Typography>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {summary.researchSummary?.summary && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Final Summary
                  </Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {summary.researchSummary.summary}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {(!summary.researchSummary && (!summary.researchSteps || summary.researchSteps.length === 0)) && (
              <Typography>No summary or research steps available for this action.</Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};